/**
 * Geography analytics — where the orders come from, by customer country.
 *
 * Orders have no country of their own; the backend folds the customer
 * profile country onto the order aggregation (`/api/admin/analytics/geography`)
 * and returns per-country orders, revenue, customers and the share of
 * each in percent, so the UI never recomputes totals.
 *
 * Two exports:
 *   <GeographyBlock/> — full card for the Analytics page (donut + ranked
 *                       table + metric switch).
 *   <GeographyMini/>  — condensed top-5 for the admin Dashboard.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Globe2, ArrowRight } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PALETTE = ['#0e7490', '#2563eb', '#7c3aed', '#059669', '#f59e0b', '#e11d48', '#0891b2', '#4f46e5'];

const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');
const fmtMoney0 = (n) => `€${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const METRICS = [
  { id: 'orders',    label: 'Orders' },
  { id: 'revenue',   label: 'Revenue' },
  { id: 'customers', label: 'Customers' },
];

const valueOf = (row, metric) => (
  metric === 'revenue' ? (row.revenue || 0)
    : metric === 'customers' ? (row.customers || 0)
      : (row.orders || 0)
);

const formatValue = (v, metric) => (metric === 'revenue' ? fmtMoney0(v) : fmtNum(v));

/** ISO-2 chip — readable and locale-independent (no flag glyph fallbacks). */
const IsoChip = ({ iso2, idx }) => (
  <span
    className="inline-flex items-center justify-center w-7 h-5 rounded text-[10px] font-extrabold tracking-wide text-white flex-shrink-0"
    style={{ backgroundColor: PALETTE[idx % PALETTE.length] }}
  >
    {iso2 || '··'}
  </span>
);

function useGeography({ token, range, customStart, customEnd, limit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const p = new URLSearchParams({ range, limit: String(limit) });
      if (range === 'custom') {
        if (customStart) p.append('start', customStart);
        if (customEnd) p.append('end', customEnd);
      }
      const r = await fetch(`${API_URL}/api/admin/analytics/geography?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.ok ? await r.json() : null);
    } catch (_) { setData(null); }
    finally { setLoading(false); }
  }, [token, range, customStart, customEnd, limit]);

  useEffect(() => { load(); }, [load]);
  return { data, loading };
}

export function GeographyBlock({ token, range = 'this_month', customStart, customEnd }) {
  const [metric, setMetric] = useState('orders');
  const { data, loading } = useGeography({ token, range, customStart, customEnd, limit: 25 });

  const items = useMemo(() => {
    const rows = [...(data?.items || [])];
    rows.sort((a, b) => valueOf(b, metric) - valueOf(a, metric));
    return rows;
  }, [data, metric]);

  // Period totals come from the API (they cover ALL countries, not just
  // the top-25 we render), so shares and the donut centre stay honest.
  const periodTotal = useMemo(() => {
    if (metric === 'revenue') return data?.total_revenue || 0;
    if (metric === 'orders') return data?.total_orders || 0;
    return items.reduce((acc, r) => acc + (r.customers || 0), 0) + (data?.unknown?.customers || 0);
  }, [data, items, metric]);

  // Donut: top 7 + everything else (incl. countries beyond the limit and
  // profiles with no country) folded into one slice.
  const donut = useMemo(() => {
    const top = items.slice(0, 7).map((r) => ({ name: r.country, value: valueOf(r, metric) }));
    const rest = periodTotal - top.reduce((acc, d) => acc + d.value, 0);
    if (rest > 0.5) top.push({ name: 'Other countries', value: rest });
    return top.filter((d) => d.value > 0);
  }, [items, metric, periodTotal]);

  const max = items.length ? valueOf(items[0], metric) : 0;

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-5"
      data-testid="an-geography"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
            <Globe2 className="w-4 h-4 text-cyan-600" /> Geography
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Country of the ordering customer, for the selected period. Share is % of the period total.
            {data?.countries ? ` · ${fmtNum(data.countries)} countries` : ''}
          </p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="an-geo-metric-switch">
          {METRICS.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMetric(m.id)}
              data-testid={`an-geo-metric-${m.id}`}
              className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors ${
                metric === m.id ? 'bg-white text-cyan-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-slate-400 text-center py-12" data-testid="an-geo-loading">Loading geography…</p>
      ) : !items.length ? (
        <p className="text-xs text-slate-400 text-center py-12" data-testid="an-geo-empty">No orders in this window.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
          {/* Donut + headline numbers */}
          <div className="relative">
            <div className="h-[220px]" data-testid="an-geo-donut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donut} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="92%" paddingAngle={2} stroke="none">
                    {donut.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip
                    formatter={(v, n) => [
                      `${formatValue(v, metric)} · ${periodTotal ? ((v / periodTotal) * 100).toFixed(1) : 0}%`,
                      n,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                {METRICS.find((m) => m.id === metric)?.label}
              </span>
              <span className="text-lg font-extrabold text-slate-800 tabular-nums" data-testid="an-geo-total">
                {formatValue(periodTotal, metric)}
              </span>
            </div>
          </div>

          {/* Ranked list */}
          <div className="max-h-[300px] overflow-y-auto pr-1" data-testid="an-geo-list">
            {items.map((row, idx) => {
              const v = valueOf(row, metric);
              const share = periodTotal ? (v / periodTotal) * 100 : 0;
              const barPct = max ? (v / max) * 100 : 0;
              return (
                <div
                  key={row.country}
                  className="py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid={`an-geo-row-${idx}`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="font-mono text-[10px] font-bold text-slate-400 w-4 text-right flex-shrink-0">{idx + 1}</span>
                    <IsoChip iso2={row.iso2} idx={idx} />
                    <span className="truncate font-medium text-slate-800 text-sm flex-1 min-w-0">{row.country}</span>
                    <span className="text-[11px] text-slate-400 tabular-nums whitespace-nowrap hidden sm:inline">
                      {fmtNum(row.customers)} cust · {fmtMoney0(row.avg_order)} avg
                    </span>
                    <span className="font-mono text-xs font-bold text-slate-800 tabular-nums whitespace-nowrap w-20 text-right">
                      {formatValue(v, metric)}
                    </span>
                    <span className="font-mono text-xs font-extrabold text-cyan-700 tabular-nums whitespace-nowrap w-12 text-right">
                      {share.toFixed(1)}%
                    </span>
                  </div>
                  <div className="mt-1 ml-[26px] h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${barPct.toFixed(1)}%`, backgroundColor: PALETTE[idx % PALETTE.length] }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && (data?.unknown?.orders > 0 || data?.others?.countries > 0) && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100" data-testid="an-geo-footnotes">
          {data.others?.countries > 0 && (
            <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">
              +{data.others.countries} more countries · {fmtNum(data.others.orders)} orders
            </span>
          )}
          {data.unknown?.orders > 0 && (
            <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-amber-50 text-amber-700">
              {fmtNum(data.unknown.orders)} orders without a country on the profile
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** Dashboard version — top 5 countries over the last 30 days. */
export function GeographyMini({ token, onOpenAnalytics }) {
  const { data, loading } = useGeography({ token, range: 'month', limit: 5 });
  const items = data?.items || [];
  const max = items.length ? items[0].orders : 0;

  return (
    <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm" data-testid="admin-dash-geography">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
          <Globe2 className="w-3.5 h-3.5 text-cyan-600" /> Top countries
          <span className="text-[10px] font-semibold text-slate-400 normal-case tracking-normal">· last 30 days</span>
        </h3>
        <button
          type="button"
          onClick={onOpenAnalytics}
          data-testid="admin-dash-geography-all"
          className="text-[11px] font-bold text-cyan-700 hover:text-cyan-800 flex items-center gap-1"
        >
          All {data?.countries ? `(${data.countries})` : ''} <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      {loading ? (
        <p className="text-[11px] text-slate-400 py-6 text-center">Loading…</p>
      ) : !items.length ? (
        <p className="text-[11px] text-slate-400 py-6 text-center">No orders in the last 30 days.</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((row, idx) => (
            <button
              key={row.country}
              type="button"
              onClick={onOpenAnalytics}
              data-testid={`admin-dash-geo-row-${idx}`}
              className="w-full text-left group"
              title={`${row.country}: ${row.orders} orders · ${fmtMoney0(row.revenue)} · ${row.customers} customers`}
            >
              <div className="flex items-center gap-2">
                <IsoChip iso2={row.iso2} idx={idx} />
                <span className="truncate text-xs font-medium text-slate-700 flex-1 min-w-0 group-hover:text-cyan-700 transition-colors">
                  {row.country}
                </span>
                <span className="font-mono text-[11px] font-bold text-slate-700 tabular-nums">{fmtNum(row.orders)}</span>
                <span className="font-mono text-[11px] font-extrabold text-cyan-700 tabular-nums w-11 text-right">
                  {row.orders_share}%
                </span>
              </div>
              <div className="mt-0.5 ml-[38px] h-1 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${max ? (row.orders / max) * 100 : 0}%`, backgroundColor: PALETTE[idx % PALETTE.length] }}
                />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default GeographyBlock;
